import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useGlobalModal } from "../Contexts/GlobalModalContext";
import { ModalActions } from "../Modals/ModalActions";
import { Button } from "./Button";

type ConfirmActionVariant = 'danger' | 'success';

const VARIANT_CLASSES: Record<ConfirmActionVariant, string> = {
  danger: 'bg-theme-danger hover:bg-theme-danger-hover',
  success: 'bg-theme-success hover:bg-theme-success-hover',
};

interface ConfirmActionButtonProps {
  /** Async handler; should return true on success */
  onAction: () => Promise<boolean>;
  variant: ConfirmActionVariant;
  /** The action's name, used for the modal's confirm button and the failure log, e.g. "Delete" */
  actionLabel: string;
  mnemonic: string;
  confirmTitle: string;
  confirmMessage: string;
  buttonLabel: ReactNode;
  /** If false, skips the confirmation modal and runs the action immediately */
  requireConfirmation: boolean;
  disabled: boolean;
  className?: string;
  /** Tooltip and accessible name, for a button whose label is an icon */
  title?: string;
}

interface ActionConfirmationProps {
  variant: ConfirmActionVariant;
  actionLabel: string;
  mnemonic: string;
  confirmTitle: string;
  confirmMessage: string;
  onConfirm: () => Promise<boolean>;
  hideModal: () => void;
}

function ActionConfirmation({ variant, actionLabel, mnemonic, confirmTitle, confirmMessage, onConfirm, hideModal }: ActionConfirmationProps) {
  const { t } = useTranslation();
  const [isModalWorking, setIsModalWorking] = useState(false);

  const confirm = async () => {
    setIsModalWorking(true);
    const success = await onConfirm();
    setIsModalWorking(false);
    if (success) hideModal();
  };

  return (
    <form>
      <div>
        <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
          <span>{confirmTitle}</span>
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className={`text-theme-text text-sm transition ease-out mb-2 ml-1 opacity-${isModalWorking ? 100 : 0}`} />
        </h2>
        <p className="text-theme-text">{confirmMessage}</p>
      </div>
      <ModalActions>
        <Button
          variant="ghost"
          onClick={hideModal}
          className="bg-theme-surface hover:bg-theme-surface-2 !font-normal"
        >
          {t("actions.cancel")}
        </Button>
        <Button variant={variant} isDefaultAction mnemonic={mnemonic} onClick={confirm}>
          {actionLabel}
        </Button>
      </ModalActions>
    </form>
  );
}

export function ConfirmActionButton({
  onAction,
  variant,
  actionLabel,
  mnemonic,
  confirmTitle,
  confirmMessage,
  buttonLabel,
  requireConfirmation,
  disabled,
  className = '',
  title,
}: ConfirmActionButtonProps) {
  const { showModal, hideModal } = useGlobalModal();
  const [isWorking, setIsWorking] = useState(false);

  const run = async () => {
    setIsWorking(true);
    const success = await onAction().catch((error: unknown) => {
      console.error(`${actionLabel} failed`, error);
      return false;
    });
    setIsWorking(false);
    return success;
  };

  const handleClick = async () => {
    if (!requireConfirmation) {
      await run();
      return;
    }

    showModal({
      content: <ActionConfirmation
        variant={variant}
        actionLabel={actionLabel}
        mnemonic={mnemonic}
        confirmTitle={confirmTitle}
        confirmMessage={confirmMessage}
        onConfirm={run}
        hideModal={hideModal} />
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isWorking || disabled}
      title={title}
      aria-label={title}
      className={`relative px-4 py-2 ${VARIANT_CLASSES[variant]} text-white font-bold rounded-sm transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className={`text-theme-text text-sm transition ease-out opacity-${isWorking ? 100 : 0}`} />
      </span>
      <span className={`${isWorking ? 'opacity-0' : 'opacity-100'} transition ease-out`}>{buttonLabel}</span>
    </button>
  );
}
